/*
 * Produktive HMI-Route: `/plants/:id/hmi`.
 *
 * Der Gegenpol zu `/hmi/preview`. Die Preview ist der Widget-Katalog gegen
 * einen Simulator; diese Seite rendert ein gespeichertes HMI-Projekt gegen
 * die echten Live-Werte einer Anlage.
 *
 * Aufbau von aussen nach innen:
 *
 *   AppShell → PlantSubNav → Projekt-Wähler → Screen-Tabs
 *     → LivePlantBindingProvider (Hub-Abo + Werte-Snapshot + Live-Quelle)
 *       → Grid pro Screen
 *         → WidgetHost je Widget (holt die Quelle aus dem Context)
 *
 * Was diese Seite bewusst NICHT ist: ein Layout-Editor (J.8) und kein
 * AI-Accept-Flow (J.6). Sie legt leere Projekte an und löscht sie, damit die
 * Route ohne die beiden Slices benutzbar ist — das Bearbeiten der Screens
 * gehört dem Editor.
 *
 * Rechte: gelesen wird von jedem angemeldeten Benutzer, der die Anlage sieht
 * (der Data-Scope-Filter serverseitig entscheidet das). Anlegen und Löschen
 * hängen an `plants.schema.edit` — dieselbe Permission, die auch das
 * Datenpunkt-Schema der Anlage öffnet (`PlantSchemaPage.tsx:141`). Die
 * Prüfung hier ist nur die Sichtbarkeit der Buttons; die Endpoints prüfen
 * unabhängig davon selbst.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { Card, CardHeader, CardBody } from '../components/ui/Card';
import { showToast } from '../components/Toaster';
import { showConfirm } from '../components/Confirmer';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import type { PlantSchemaResponse } from '../api/types';
import { sessionSignal } from '../auth/session';
import { LivePlantBindingProvider } from '../hmi/PointBindingProvider';
import { WithSimulation } from '../hmi/WithSimulation';
import { SIM_PREFIX } from '../hmi/simPoints';
import { WidgetHost } from '../hmi/WidgetHost';
import { collectBindingKeys, parseBindingKey } from '../hmi/bindingKey';
import { describeStalePolicyDe, staleAfterMsFor, type HmiLeafInfo } from '../hmi/staleness';
import { buildLeafMetaIndex, type HmiLeafMeta } from '../hmi/leafMeta';
import { LeafMetaProvider } from '../hmi/LeafMetaContext';
import { GRID_SPECS, frameOfLayout } from '../hmi/editor/editorModel';
import {
  createHmiProject,
  deleteHmiProject,
  getHmiProject,
  listHmiProjects,
  parseProjectBody,
  type HmiProjectSummary,
} from '../hmi/projectApi';
import type { HmiScreen, LayoutTarget, WidgetDefinition, WidgetPlacement } from '../hmi/widgets';
import { hmiAppearanceAttributes } from '../hmi/appearanceModel';
import { pickPlantTarget } from '../hmi/plantTarget';
import { FreeStage } from '../hmi/panel/FreeStage';
import { ThemeScope, useEffectiveScheme, useStoredScheme } from '../hmi/panel/ThemeScope';
import { DEFAULT_RUNTIME, HmiRuntimeProvider, type HmiRuntime } from '../hmi/panel/runtimeContext';
import type { HmiAppearance } from '../hmi/appearanceModel';
import '../hmi/hmiCell.css';
import './HmiPlantPage.css';

interface Props {
  /** Route-Parameter `:id`. */
  id?: string;
}

/**
 * Layout target of this page: chosen per screen by `pickPlantTarget`
 * (hmi/plantTarget.ts) from the window width, a phone held sideways, and
 * the layouts the screen has. Below {@link MOBILE_MAX_WIDTH_PX} the
 * `mobileResponsive` layout applies if there is one; otherwise the desktop
 * grid is stacked in one column (see {@link ScreenGrid}). A screen that only
 * has panel layouts shows its panel layout; free layouts are scaled to the
 * page width.
 */
const DESKTOP_TARGET: LayoutTarget = 'desktopResponsive';
const MOBILE_MAX_WIDTH_PX = 720;

/** A phone held sideways: low window in landscape. */
const PHONE_LANDSCAPE_QUERY = '(max-height: 500px) and (orientation: landscape)';

/** `true` while the window is narrower than the mobile breakpoint. */
function useNarrowViewport(): boolean {
  return useMediaQuery(`(max-width: ${MOBILE_MAX_WIDTH_PX}px)`);
}

function useMediaQuery(query: string): boolean {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return narrow;
}

export function HmiPlantPage({ id }: Props) {
  const plantId = id ?? '';
  const canEdit = sessionSignal.value.user?.permissions.includes('plants.schema.edit') ?? false;

  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedScreenId, setSelectedScreenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const projectsState = useAsync(
    (signal) => (plantId ? listHmiProjects(plantId, signal) : Promise.resolve({ projects: [] })),
    [plantId, refreshTick],
  );

  const projects: HmiProjectSummary[] = projectsState.status === 'ok'
    ? projectsState.data.projects
    : [];

  // Erstes Projekt vorauswählen, sobald die Liste da ist. Wenn das aktuell
  // gewählte Projekt aus der Liste verschwindet (gelöscht, umgezogen), fällt
  // die Auswahl auf das erste zurück statt auf einen 404-Detailabruf.
  useEffect(() => {
    if (projects.length === 0) {
      if (selectedProjectId !== null) setSelectedProjectId(null);
      return;
    }
    if (!selectedProjectId || !projects.some((p) => p.id === selectedProjectId)) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  const projectState = useAsync(
    (signal) => (selectedProjectId ? getHmiProject(selectedProjectId, signal) : Promise.resolve(null)),
    [selectedProjectId, refreshTick],
  );

  // Anlagen-Schema für drei Zwecke: Symbol-Auflösung in den Command-Toasts
  // (ohne das steht dort der nackte Leaf-Guid), die Binding-Diagnose
  // ("zeigt auf einen Datenpunkt, den es in dieser Anlage nicht gibt") und
  // — sicherheitsrelevant — die Alters-Schwelle je Datenpunkt aus
  // Poll-Intervall und Heartbeat (`hmi/staleness.ts`).
  //
  // Scheitert der Abruf, läuft die Seite weiter: die Live-Werte hängen
  // nicht davon ab, die Toasts zeigen dann Guids, und für das Altern gilt
  // der konservative Fallback. Das ist der Grund, warum der Fallback
  // konservativ sein MUSS und nicht knapp: ein fehlgeschlagener
  // Schema-Abruf darf nicht die halbe Anlage grau färben.
  const schemaState = useAsync(
    (signal) => (plantId
      ? api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, signal)
      : Promise.resolve(null)),
    [plantId],
  );

  const leafIndex = useMemo(
    () => buildLeafMetaIndex(schemaState.status === 'ok' ? schemaState.data : null),
    [schemaState.status === 'ok' ? schemaState.data : null],
  );

  // Stabile Funktion, damit die Live-Quelle im Provider nicht bei jedem
  // Render neu gebaut wird (das würde jedes Punkt-Abo neu öffnen).
  const leafInfoFor = useMemo(
    () => (leafId: string): HmiLeafMeta | undefined => leafIndex.get(leafId),
    [leafIndex],
  );

  const narrow = useNarrowViewport();
  const phoneLandscape = useMediaQuery(PHONE_LANDSCAPE_QUERY);

  const parsed = useMemo(() => {
    if (projectState.status !== 'ok' || projectState.data === null) return null;
    return parseProjectBody(projectState.data.bodyJson);
  }, [projectState.status === 'ok' ? projectState.data : null]);

  const widgets: WidgetDefinition[] = parsed?.ok ? parsed.widgets : [];
  const screens: HmiScreen[] = parsed?.ok ? parsed.screens : [];

  useEffect(() => {
    if (screens.length === 0) {
      if (selectedScreenId !== null) setSelectedScreenId(null);
      return;
    }
    if (!selectedScreenId || !screens.some((s) => s.id === selectedScreenId)) {
      setSelectedScreenId(screens[0].id);
    }
  }, [screens, selectedScreenId]);

  const activeScreen = screens.find((s) => s.id === selectedScreenId) ?? null;

  // What the free-design widgets need: the nav-bar lists the screens and
  // switches the tab, buttons may write (editing false), images load from
  // the asset endpoint.
  const [storedScheme, setScheme] = useStoredScheme(`hmi-scheme:${plantId}`);
  const scheme = useEffectiveScheme(parsed?.ok ? parsed.appearance : undefined, storedScheme);
  const runtime = useMemo<HmiRuntime>(() => ({
    ...DEFAULT_RUNTIME,
    screens: screens.map((s) => ({ id: s.id, label: s.label, iconRef: s.iconRef })),
    currentScreenId: selectedScreenId,
    navigate: (id: string) => setSelectedScreenId(id),
    assetUrl: (id: string) => `/api/hmi-assets/${encodeURIComponent(id)}/content`,
    editing: false,
    scheme,
    setScheme,
  }), [screens, selectedScreenId, scheme, setScheme]);
  const widgetsById = useMemo(
    () => new Map(widgets.map((w) => [w.id, w] as const)),
    [widgets],
  );

  const diagnosis = useMemo(
    () => diagnoseBindings(widgets, plantId, leafIndex),
    [widgets, plantId, leafIndex],
  );

  async function onCreateProject() {
    if (creating) return;
    setCreating(true);
    try {
      const created = await createHmiProject(plantId, {
        name: suggestProjectName(projects),
        description: 'Leer angelegt — Screens kommen aus dem Layout-Editor oder dem AI-Vorschlag.',
      });
      showToast({ tone: 'success', title: `HMI-Projekt "${created.name}" angelegt`, autoHideAfterMs: 4000 });
      setSelectedProjectId(created.id);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      showToast({
        tone: 'error',
        title: 'Anlegen fehlgeschlagen',
        message: describeError(err),
        autoHideAfterMs: 6000,
      });
    } finally {
      setCreating(false);
    }
  }

  async function onDeleteProject(project: HmiProjectSummary) {
    const ok = await showConfirm({
      title: 'HMI-Projekt löschen?',
      message: `"${project.name}" wird unwiderruflich entfernt. `
        + 'Die Datenpunkte und die hochgeladenen Assets bleiben unberührt.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteHmiProject(project.id);
      showToast({ tone: 'success', title: 'HMI-Projekt gelöscht', autoHideAfterMs: 3500 });
      setSelectedProjectId(null);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      showToast({
        tone: 'error',
        title: 'Löschen fehlgeschlagen',
        message: describeError(err),
        autoHideAfterMs: 6000,
      });
    }
  }

  if (!plantId) {
    return (
      <AppShell title="HMI">
        <Card>
          <CardBody>
            <p class="muted">Keine Anlage in der Route. Über <a href="/plants">Anlagen</a> eine auswählen.</p>
          </CardBody>
        </Card>
      </AppShell>
    );
  }

  const plantName = schemaState.status === 'ok' ? (schemaState.data?.name ?? '') : '';

  return (
    <AppShell title="HMI">
      <PlantSubNav plantId={plantId} plantName={plantName} />

      <div class="hv-hmi-plant">
        <Card padded={false}>
          <CardHeader>
            <span class="hv-hmi-plant__card-title grow">HMI-Projekte</span>
            {canEdit && (
              /* Der Weg zum Editor. Eigene Seite und kein Modus hier: auf
                 dieser Seite laufen die Widgets gegen die echte Anlage, ein
                 Klick ist ein Schaltbefehl. Im Editor ist derselbe Klick eine
                 Auswahl. Beides zu mischen waere der kuerzeste Weg zu einer
                 Pumpe, die jemand beim Anwaehlen startet. */
              <a href={`/plants/${plantId}/hmi/editor`} class="hv-btn hv-btn--sm">
                Bearbeiten
              </a>
            )}
            {canEdit && (
              <button type="button" class="hv-btn hv-btn--sm" disabled={creating}
                      onClick={() => void onCreateProject()}>
                {creating ? 'Anlegen…' : 'Leeres Projekt anlegen'}
              </button>
            )}
          </CardHeader>
          <CardBody>
            {projectsState.status === 'loading' && <p class="muted">Projekte werden geladen…</p>}
            {projectsState.status === 'error' && (
              <p class="hv-hmi-plant__error">
                Projekte konnten nicht geladen werden. {describeError(projectsState.error)}
              </p>
            )}
            {projectsState.status === 'ok' && projects.length === 0 && (
              <p class="muted">
                Für diese Anlage ist noch kein HMI-Projekt gespeichert.
                {canEdit
                  ? ' Ein leeres anlegen und die Screens danach im Layout-Editor oder per AI-Vorschlag füllen.'
                  : ' Anlegen braucht die Berechtigung plants.schema.edit.'}
              </p>
            )}
            {projects.length > 0 && (
              <div class="hv-hmi-plant__projects" role="tablist" aria-label="HMI-Projekte">
                {projects.map((project) => (
                  <div key={project.id} class="hv-hmi-plant__project-row">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={project.id === selectedProjectId}
                      class={`hv-hmi-plant__project${project.id === selectedProjectId ? ' hv-hmi-plant__project--active' : ''}`}
                      onClick={() => setSelectedProjectId(project.id)}
                      title={project.description ?? undefined}
                    >
                      <span class="hv-hmi-plant__project-name">{project.name}</span>
                      <span class="hv-hmi-plant__project-meta">
                        {project.widgetCount} Widgets · {project.screenCount} Screens · Rev. {project.revision}
                      </span>
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        class="hv-btn hv-btn--danger hv-btn--sm"
                        onClick={() => void onDeleteProject(project)}
                        title="Projekt löschen"
                      >
                        Löschen
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {projectState.status === 'error' && (
          <Card>
            <CardBody>
              <p class="hv-hmi-plant__error">
                Projekt konnte nicht geladen werden. {describeError(projectState.error)}
              </p>
            </CardBody>
          </Card>
        )}

        {parsed && !parsed.ok && (
          <Card>
            <CardBody>
              <p class="hv-hmi-plant__error">Projekt-Rumpf ist unbrauchbar: {parsed.error}</p>
            </CardBody>
          </Card>
        )}

        {parsed?.ok && (
          <Card padded={false}>
            <CardHeader>
              <span class="hv-hmi-plant__card-title grow">
                {projectState.status === 'ok' && projectState.data ? projectState.data.name : 'Screen'}
              </span>
              <BindingDiagnosis diagnosis={diagnosis} schemaKnown={schemaState.status === 'ok'} />
            </CardHeader>
            <CardBody>
              {screens.length === 0 && (
                <p class="muted">
                  Das Projekt hat {widgets.length} Widget(s), aber keinen Screen. Ohne Screen gibt es
                  keine Platzierung und damit nichts zu rendern — Screens legt der Layout-Editor an.
                </p>
              )}

              {screens.length > 1 && (
                <div class="hv-hmi-plant__screens" role="tablist" aria-label="Screens">
                  {screens.map((screen) => (
                    <button
                      key={screen.id}
                      type="button"
                      role="tab"
                      aria-selected={screen.id === selectedScreenId}
                      class={`hv-hmi-plant__screen${screen.id === selectedScreenId ? ' hv-hmi-plant__screen--active' : ''}`}
                      onClick={() => setSelectedScreenId(screen.id)}
                    >
                      {screen.label}
                    </button>
                  ))}
                </div>
              )}

              {activeScreen && (
                <LivePlantBindingProvider plantId={plantId} leafInfoFor={leafInfoFor}>
                  <WithSimulation>
                  <LeafMetaProvider byLeafId={leafInfoFor}>
                    <HmiRuntimeProvider value={runtime}>
                      <ThemeScope appearance={parsed.appearance} scheme={storedScheme} class="hv-hmi-plant__theme">
                        <ScreenGrid screen={activeScreen} widgetsById={widgetsById}
                                    leafInfoFor={leafInfoFor} narrow={narrow}
                                    phoneLandscape={phoneLandscape}
                                    appearance={parsed.appearance} />
                      </ThemeScope>
                    </HmiRuntimeProvider>
                  </LeafMetaProvider>
                  </WithSimulation>
                </LivePlantBindingProvider>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    </AppShell>
  );
}

// ------------------------------------------------------------------ Screen

interface ScreenGridProps {
  screen: HmiScreen;
  widgetsById: Map<string, WidgetDefinition>;
  leafInfoFor: (leafId: string) => HmiLeafInfo | undefined;
  /** Schmales Fenster: Handy-Layout bevorzugen. */
  narrow: boolean;
  /** Handy quer: das Querformat bevorzugen, wenn es eines gibt. */
  phoneLandscape: boolean;
  /** Colour scheme and corners set by the author. */
  appearance?: HmiAppearance;
}

/**
 * Rendert einen Screen als CSS-Grid.
 *
 * Die Platzierungen kommen aus dem Layout-Eintrag für das Ziel, das
 * {@link useNarrowViewport} wählt. Fehlt der Eintrag, oder fehlt für ein
 * Widget die Platzierung, landet das Widget im Auto-Flow — sichtbar, nur
 * nicht an der gewünschten Stelle. Ein verstecktes Widget wäre für den Autor
 * nicht unterscheidbar von "Widget-Id zeigt ins Leere".
 *
 * Row height matches the editor ({@link GRID_SPECS}): there `rowSpan` means
 * "this many 80 px rows", and that is how it is drawn here. Rows used to be
 * as tall as their content, so a tile the author had stretched to three rows
 * was not three rows tall in operation.
 */
function ScreenGrid({ screen, widgetsById, leafInfoFor, narrow, phoneLandscape, appearance }: ScreenGridProps) {
  // Which layout: see hmi/plantTarget.ts. A screen drawn only for the panel
  // (free, TP8 portrait or landscape) shows too, scaled to the page width.
  const choice = pickPlantTarget(screen.layouts, { narrow, phoneLandscape });
  const layout = choice.layout;
  const target: LayoutTarget = choice.target;
  const desktopLayout = screen.layouts.find((l) => l.target === DESKTOP_TARGET) ?? null;
  // Narrow window without a mobile layout: the twelve-column desktop grid
  // would be unreadable at 390 px. Stack instead, in desktop reading order.
  const stackFallback = choice.stack;

  if (layout && layout.mode === 'free') {
    // As wide as the card, but never larger than the device itself and
    // never taller than most of the window: a portrait TP8 at full card
    // width would be two screens high.
    const frame = frameOfLayout(layout);
    const maxWidth = `min(100%, ${frame.width}px, calc(85vh * ${(frame.width / frame.height).toFixed(4)}))`;
    return (
      <div class="hv-hmi-plant__free" style={{ maxWidth }} {...hmiAppearanceAttributes(appearance)}>
        <FreeStage key={`${screen.id}-${layout.target}`} layout={layout} widgetIds={screen.widgetIds}
                   widgetsById={widgetsById} fit="width" />
      </div>
    );
  }
  const placements: Record<string, WidgetPlacement> = stackFallback ? {} : (layout?.placements ?? {});
  const columns = stackFallback ? 1 : (layout?.canvasSize?.columns ?? GRID_SPECS[target].columns);
  const rowHeight = GRID_SPECS[target].cellHeightPx;
  const order = stackFallback ? stackOrder(screen.widgetIds, desktopLayout?.placements ?? {}) : screen.widgetIds;

  const missingIds = screen.widgetIds.filter((widgetId) => !widgetsById.has(widgetId));

  return (
    <>
      {missingIds.length > 0 && (
        <p class="hv-hmi-plant__warn" role="alert">
          {missingIds.length} Widget-Id(s) in diesem Screen zeigen auf kein Widget im Projekt:
          {' '}<span class="mono">{missingIds.join(', ')}</span>
        </p>
      )}
      <div
        class="hv-hmi-plant__grid"
        {...hmiAppearanceAttributes(appearance)}
        style={{
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gridAutoRows: `minmax(${rowHeight}px, auto)`,
        }}
      >
        {order.map((widgetId) => {
          const widget = widgetsById.get(widgetId);
          if (!widget) return null;
          const placement = placements[widgetId];
          const style = placement
            ? {
                gridColumn: `${placement.col + 1} / span ${Math.min(columns, Math.max(1, placement.colSpan))}`,
                gridRow: `${placement.row + 1} / span ${Math.max(1, placement.rowSpan)}`,
              }
            : { gridColumn: stackFallback ? '1 / -1' : `span ${Math.min(columns, 3)}` };
          return (
            <div key={widgetId} class="hv-hmi-plant__cell hv-hmi-cell" style={style}
                 title={describeWidgetStalePolicy(widget, leafInfoFor)}>
              <WidgetHost widget={widget} />
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * Order for the stack on narrow windows: row by row, then left to right, the
 * way one reads the desktop layout. Widgets without a placement go last, in
 * project order.
 */
function stackOrder(ids: readonly string[], placements: Record<string, WidgetPlacement>): string[] {
  return ids
    .map((id, index) => ({ id, index, p: placements[id] }))
    .sort((a, b) => {
      if (a.p && b.p) return a.p.row - b.p.row || a.p.col - b.p.col || a.index - b.index;
      if (a.p) return -1;
      if (b.p) return 1;
      return a.index - b.index;
    })
    .map((x) => x.id);
}

/**
 * Tooltip-Text zur Alters-Politik einer Kachel.
 *
 * Genommen wird der Datenpunkt mit der KLEINSTEN Schwelle — der dimmt
 * zuerst, und genau nach dem fragt der Techniker, wenn eine Kachel grau
 * ist. Ohne diese Erklärung ist eine gedimmte Kachel nicht handlungsfähig:
 * "Anlage antwortet nicht" und "Schwelle ist zu knapp konfiguriert" sehen
 * identisch aus, brauchen aber gegenteilige Reaktionen.
 *
 * `undefined` (kein Tooltip), wenn das Widget keine auflösbaren Bindings
 * hat — dann sagt schon die Kachel selbst, dass nichts kommt.
 */
function describeWidgetStalePolicy(
  widget: WidgetDefinition,
  leafInfoFor: (leafId: string) => HmiLeafInfo | undefined,
): string | undefined {
  let tightest: { threshold: number; info: HmiLeafInfo | undefined } | null = null;
  for (const key of collectBindingKeys(widget)) {
    const parsedKey = parseBindingKey(key);
    if (!parsedKey) continue;
    const info = leafInfoFor(parsedKey.leafId);
    const threshold = staleAfterMsFor(info);
    if (tightest === null || threshold < tightest.threshold) {
      tightest = { threshold, info };
    }
  }
  return tightest === null ? undefined : describeStalePolicyDe(tightest.info);
}

// -------------------------------------------------------------- Diagnose

interface Diagnosis {
  total: number;
  live: number;
  demo: number;
  malformed: number;
  foreignPlant: number;
  unknownLeaf: number;
}

/**
 * Zählt die Binding-Keys eines Projekts nach Auflösbarkeit.
 *
 * Der Grund für diese Anzeige: ein Widget mit einem unauflösbaren Binding
 * rendert eine graue Kachel. Bei vierzig Kacheln fällt nicht auf, ob drei
 * davon grau sind, weil die Anlage schweigt oder weil das Projekt auf
 * Datenpunkte einer anderen Anlage zeigt. Die zwei Fälle brauchen völlig
 * verschiedene Reaktionen, also müssen sie unterscheidbar sein.
 */
function diagnoseBindings(
  widgets: readonly WidgetDefinition[],
  plantId: string,
  leafIndex: ReadonlyMap<string, HmiLeafInfo>,
): Diagnosis {
  const keys = new Set<string>();
  for (const widget of widgets) {
    // "sim/..." sind Schauwerte der Simulation (simPoints.ts), keine
    // Bindungen an die Anlage; sie werden weder gezaehlt noch geprueft.
    for (const key of collectBindingKeys(widget)) if (!key.trim().toLowerCase().startsWith(SIM_PREFIX)) keys.add(key);
  }

  const diagnosis: Diagnosis = {
    total: keys.size, live: 0, demo: 0, malformed: 0, foreignPlant: 0, unknownLeaf: 0,
  };
  const expectedPlant = plantId.toLowerCase();

  for (const key of keys) {
    const parsedKey = parseBindingKey(key);
    if (!parsedKey) {
      if (key.trim().toLowerCase().startsWith('demo/')) diagnosis.demo += 1;
      else diagnosis.malformed += 1;
      continue;
    }
    if (parsedKey.plantId !== expectedPlant) {
      diagnosis.foreignPlant += 1;
      continue;
    }
    // Nur prüfen, wenn das Schema geladen ist — ein leerer Index würde sonst
    // jedes Binding als unbekannt melden.
    if (leafIndex.size > 0 && !leafIndex.has(parsedKey.leafId)) {
      diagnosis.unknownLeaf += 1;
      continue;
    }
    diagnosis.live += 1;
  }
  return diagnosis;
}

function BindingDiagnosis({ diagnosis, schemaKnown }: { diagnosis: Diagnosis; schemaKnown: boolean }) {
  if (diagnosis.total === 0) return null;
  const problems = diagnosis.demo + diagnosis.malformed + diagnosis.foreignPlant + diagnosis.unknownLeaf;
  const tone = problems === 0 ? 'ok' : 'warn';
  const parts: string[] = [];
  if (diagnosis.demo > 0) parts.push(`${diagnosis.demo}× Simulator-Key (demo/…)`);
  if (diagnosis.malformed > 0) parts.push(`${diagnosis.malformed}× ungültiges Format`);
  if (diagnosis.foreignPlant > 0) parts.push(`${diagnosis.foreignPlant}× fremde Anlage`);
  if (diagnosis.unknownLeaf > 0) parts.push(`${diagnosis.unknownLeaf}× Datenpunkt unbekannt`);

  return (
    <span class={`hv-hmi-plant__diag hv-hmi-plant__diag--${tone}`}
          title={schemaKnown
            ? 'Auflösbarkeit der Binding-Keys gegen das Anlagen-Schema geprüft.'
            : 'Anlagen-Schema nicht geladen — Datenpunkt-Existenz konnte nicht geprüft werden.'}>
      {diagnosis.live}/{diagnosis.total} Bindings auflösbar
      {parts.length > 0 && <> — {parts.join(', ')}</>}
    </span>
  );
}

// --------------------------------------------------------------- Helfer

/** Nächster freier Standardname. Verhindert die 409 aus dem Unique-Index
 *  beim zweiten Klick auf "Leeres Projekt anlegen". */
function suggestProjectName(existing: readonly HmiProjectSummary[]): string {
  const taken = new Set(existing.map((p) => p.name));
  if (!taken.has('HMI')) return 'HMI';
  for (let i = 2; i < 100; i++) {
    const candidate = `HMI ${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `HMI ${Date.now()}`;
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'Keine Berechtigung (plants.schema.edit fehlt).';
    if (err.status === 404) return 'Anlage oder Projekt nicht gefunden.';
    if (err.status === 409) return 'Ein Projekt mit diesem Namen existiert für die Anlage bereits.';
    const body = err.body as { error?: string } | null;
    return body?.error ?? `Server antwortete mit ${err.status}.`;
  }
  return err instanceof Error ? err.message : 'Unbekannter Fehler.';
}
